import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Collapse,
  Grid,
  TextField,
  Switch,
  Autocomplete,
  Box,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Stack,
} from "@mui/material";
import TopicIcon from "@mui/icons-material/Topic";
import EventIcon from "@mui/icons-material/Event";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { formatDateForAPI } from "../../../utils/hooks/dataHandler";
import { JustInfo } from "../../utils/Icon";
import { getFullYearStartFromEnd } from "../../../utils/hooks/financialYear";

const EXCHANGE_RATE_ERROR_HELPER_TEXT = {
  NO_DATA:
    "MAS has no published rates for this financial period. Please enter the exchange rate manually.",
  UNSUPPORTED_CURRENCY:
    "This currency is not supported by MAS for automated rates. Please enter the exchange rate manually.",
  AUTH_ERROR:
    "Your session could not authorize this request. Sign in again, or enter the rate manually.",
  MISSING_API_KEY:
    "The exchange rate service is not configured. Contact your administrator, or enter the rate manually.",
  INVALID_DATE:
    "The financial year start or end date is invalid. Correct the dates, or enter the rate manually.",
  INVALID_DATE_RANGE:
    "The financial year date range is not valid for this request. Adjust the basis period, or enter the rate manually.",
  API_ERROR:
    "The MAS exchange rate service is temporarily unavailable. Please try again later or enter the exchange rate manually.",
};

const normalizeIsFullYear = (value) => {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;
  if (typeof value === "string") {
    const normalizedValue = value.trim().toLowerCase();
    if (["true", "1", "yes"].includes(normalizedValue)) return true;
    if (["false", "0", "no"].includes(normalizedValue)) return false;
  }
  return null;
};

const isSameCalendarDate = (left, right) => {
  if (!left || !right) return false;

  const leftDate = new Date(left);
  const rightDate = new Date(right);

  if (Number.isNaN(leftDate.getTime()) || Number.isNaN(rightDate.getTime())) {
    return false;
  }

  return (
    leftDate.getFullYear() === rightDate.getFullYear() &&
    leftDate.getMonth() === rightDate.getMonth() &&
    leftDate.getDate() === rightDate.getDate()
  );
};

const inferIsFullYearFromDates = (financialYearStart, financialYearEnd) => {
  if (!financialYearStart || !financialYearEnd) return true;

  const expectedStart = getFullYearStartFromEnd(financialYearEnd);
  if (!expectedStart) return true;

  return isSameCalendarDate(financialYearStart, expectedStart);
};

const isBasisPeriodMoreThanTwelveMonths = (
  financialYearStart,
  financialYearEnd,
) => {
  if (!financialYearStart || !financialYearEnd) return false;

  const start = new Date(financialYearStart);
  const currentYABasisStart = getFullYearStartFromEnd(financialYearEnd);
  if (
    Number.isNaN(start.getTime()) ||
    !currentYABasisStart
  ) {
    return false;
  }

  return start < currentYABasisStart;
};

const getCrossYABasisPeriods = (
  financialYearStart,
  financialYearEnd,
  yearOfAssessment,
) => {
  if (!financialYearStart || !financialYearEnd || !yearOfAssessment) {
    return null;
  }

  const start = new Date(financialYearStart);
  const end = new Date(financialYearEnd);
  const ya = Number(yearOfAssessment);
  if (
    Number.isNaN(start.getTime()) ||
    Number.isNaN(end.getTime()) ||
    !Number.isInteger(ya) ||
    end.getFullYear() !== ya - 1
  ) {
    return null;
  }

  const currentStart = getFullYearStartFromEnd(end);
  if (!currentStart || start >= currentStart) return null;

  const precedingEnd = new Date(currentStart);
  precedingEnd.setDate(precedingEnd.getDate() - 1);

  return {
    preceding: {
      start,
      end: precedingEnd,
      ya: ya - 1,
    },
    current: {
      start: currentStart,
      end,
      ya,
    },
  };
};

const getCustomizedBasisPeriods = (
  basisPeriods,
  financialYearStart,
  financialYearEnd,
  yearOfAssessment,
) => {
  if (!Array.isArray(basisPeriods) || basisPeriods.length !== 2) return null;

  const periods = [...basisPeriods].sort(
    (left, right) =>
      Number(left?.yearOfAssessment) - Number(right?.yearOfAssessment),
  );
  const splitEnd = new Date(periods[0]?.financialYearEnd);
  const start = new Date(financialYearStart);
  const end = new Date(financialYearEnd);
  const ya = Number(yearOfAssessment);

  if (
    [splitEnd, start, end].some((date) => Number.isNaN(date.getTime())) ||
    splitEnd < start ||
    splitEnd >= end ||
    !Number.isInteger(ya)
  ) {
    return null;
  }

  const currentStart = new Date(splitEnd);
  currentStart.setDate(currentStart.getDate() + 1);

  return {
    preceding: { start, end: splitEnd, ya: ya - 1 },
    current: { start: currentStart, end, ya },
  };
};

const toBasisPeriodsArray = (periods) =>
  periods
    ? [periods.preceding, periods.current].map((period) => ({
        yearOfAssessment: period.ya,
        financialYearStart: period.start,
        financialYearEnd: period.end,
      }))
    : [];

const formatBasisPeriodDate = (date) =>
  date.toLocaleDateString("en-SG", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });

// react-datepicker uses Floating UI's ResizeObserver by default. Inside a
// scrolling MUI Dialog that observer can repeatedly resize and reposition the
// calendar. Resize/scroll listeners are sufficient for these fixed-size inputs.
const mountStableDatePickerPopper = (
  referenceElement,
  floatingElement,
  update,
) => {
  if (!referenceElement || !floatingElement) return undefined;

  let frameId;
  const scheduleUpdate = () => {
    window.cancelAnimationFrame(frameId);
    frameId = window.requestAnimationFrame(update);
  };
  const scrollContainer = referenceElement.closest(".MuiDialogContent-root");

  scheduleUpdate();
  window.addEventListener("resize", scheduleUpdate);
  scrollContainer?.addEventListener("scroll", scheduleUpdate, {
    passive: true,
  });

  return () => {
    window.cancelAnimationFrame(frameId);
    window.removeEventListener("resize", scheduleUpdate);
    scrollContainer?.removeEventListener("scroll", scheduleUpdate);
  };
};

const stableDatePickerPopperProps = {
  strategy: "fixed",
  whileElementsMounted: mountStableDatePickerPopper,
};

const TaxComputationDialog = ({
  open,
  onClose,
  onExited,
  isEditMode,
  formData,
  currencyList,
  onSubmit,
  onFormChange,
  getExchangeRate,
  submitLoading = false,
}) => {
  const {
    financialYearEnd,
    financialYearStart,
    yearOfAssessment,
    isFullYear,
    taxComputationType,
    taxPeriodType = "single_ya",
    basisPeriods = [],
    isCustomizedBasisPeriod = false,
    currency,
    currencyRate,
  } = formData;

  const [masCurrencyRate, setMasCurrencyRate] = useState(null);
  const [rateLoading, setRateLoading] = useState(false);
  const [showRateDetails, setShowRateDetails] = useState(false);
  const [manualInputRequired, setManualInputRequired] = useState(false);
  const [rateErrorMessage, setRateErrorMessage] = useState("");

  const currentYear = new Date().getFullYear();
  const explicitIsFullYear = normalizeIsFullYear(isFullYear);
  const resolvedIsFullYear =
    explicitIsFullYear ??
    inferIsFullYearFromDates(financialYearStart, financialYearEnd);
  const isCrossYA = taxPeriodType === "cross_ya";
  const parsedFinancialYearEnd = financialYearEnd
    ? new Date(financialYearEnd)
    : null;
  const parsedFinancialYearStart = financialYearStart
    ? new Date(financialYearStart)
    : null;
  const hasValidFinancialYearEnd =
    parsedFinancialYearEnd && !Number.isNaN(parsedFinancialYearEnd.getTime());
  const hasValidFinancialYearStart =
    parsedFinancialYearStart &&
    !Number.isNaN(parsedFinancialYearStart.getTime());
  const financialYearStartMaxDate = hasValidFinancialYearEnd
    ? isCrossYA
      ? (() => {
          const currentStart = getFullYearStartFromEnd(
            parsedFinancialYearEnd,
          );
          if (!currentStart) return parsedFinancialYearEnd;
          const maxDate = new Date(currentStart);
          maxDate.setDate(maxDate.getDate() - 1);
          return maxDate;
        })()
      : parsedFinancialYearEnd
    : null;
  const hasValidFinancialPeriod = Boolean(
    hasValidFinancialYearEnd &&
    hasValidFinancialYearStart &&
    parsedFinancialYearStart <= parsedFinancialYearEnd,
  );
  const hasValidAutomaticCrossYAPeriod =
    isBasisPeriodMoreThanTwelveMonths(financialYearStart, financialYearEnd) &&
    Number(yearOfAssessment) - 1 === new Date(financialYearEnd).getFullYear();
  const automaticCrossYABasisPeriods = isCrossYA
    ? getCrossYABasisPeriods(
        financialYearStart,
        financialYearEnd,
        yearOfAssessment,
      )
    : null;
  const customizedCrossYABasisPeriods = isCrossYA
    ? getCustomizedBasisPeriods(
        basisPeriods,
        financialYearStart,
        financialYearEnd,
        yearOfAssessment,
      )
    : null;
  const crossYABasisPeriods = isCustomizedBasisPeriod
    ? customizedCrossYABasisPeriods
    : automaticCrossYABasisPeriods;
  const hasValidCrossYAPeriod =
    !isCrossYA ||
    (hasValidAutomaticCrossYAPeriod &&
      (!isCustomizedBasisPeriod || Boolean(customizedCrossYABasisPeriods)));
  const crossYAPeriodError =
    isCrossYA && !hasValidCrossYAPeriod
      ? "Cross-YA requires a valid period longer than 12 months, with the financial year end falling in YA - 1."
      : "";

  useEffect(() => {
    if (!open || isFullYear === resolvedIsFullYear) return;
    onFormChange("isFullYear", resolvedIsFullYear);
  }, [open, isFullYear, resolvedIsFullYear, onFormChange]);

  useEffect(() => {
    if (!open || !isCrossYA || !resolvedIsFullYear) return;
    onFormChange("isFullYear", false);
  }, [open, isCrossYA, resolvedIsFullYear, onFormChange]);

  // When financialYearEnd is selected and isFullYear, auto-fill financialYearStart
  useEffect(() => {
    if (financialYearEnd && resolvedIsFullYear) {
      const start = getFullYearStartFromEnd(financialYearEnd);
      if (start) onFormChange("financialYearStart", start);
    }
  }, [financialYearEnd, resolvedIsFullYear]);

  useEffect(() => {
    // Reset states
    setMasCurrencyRate(null);
    setManualInputRequired(false);
    setShowRateDetails(false);
    setRateErrorMessage("");

    // SGD doesn't need exchange rate
    if (!currency || currency?.code === "SGD") {
      onFormChange("currencyRate", 1);
      return;
    }

    // Validate financial year dates
    if (!financialYearStart || !financialYearEnd) {
      setManualInputRequired(true);
      return;
    }

    const endYear = new Date(financialYearEnd).getFullYear();
    const expectedYear = (yearOfAssessment || currentYear) - 1;

    // API only provides previous year's exchange rate
    if (endYear !== expectedYear) {
      setManualInputRequired(true);
      return;
    }

    // Fetch exchange rate from API
    setRateLoading(true);
    let cancelled = false;

    const startDate = new Date(financialYearStart);
    const endDate = new Date(financialYearEnd);

    getExchangeRate(
      currency?.code,
      formatDateForAPI(startDate),
      formatDateForAPI(endDate),
    )
      .then((res) => {
        const data = res?.data;

        if (!cancelled && data?.rate != null) {
          setMasCurrencyRate(data);
          onFormChange("currencyRate", data.rate);
          setShowRateDetails(true);
        } else {
          setManualInputRequired(true);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          const errorType = err?.errorType;
          if (errorType && EXCHANGE_RATE_ERROR_HELPER_TEXT[errorType]) {
            setRateErrorMessage(EXCHANGE_RATE_ERROR_HELPER_TEXT[errorType]);
          } else {
            const axiosRes = err?.error?.response;
            const status = axiosRes?.status ?? err?.statusCode;
            const body = axiosRes?.data;
            const message = body?.message ?? body?.error ?? err?.message ?? "";
            const isUnsupportedCurrency400 =
              status === 400 &&
              typeof message === "string" &&
              message.toLowerCase().includes("unsupported currency");
            if (isUnsupportedCurrency400) {
              setRateErrorMessage(
                EXCHANGE_RATE_ERROR_HELPER_TEXT.UNSUPPORTED_CURRENCY,
              );
            } else if (typeof message === "string" && message.trim()) {
              setRateErrorMessage(
                `${message.trim()} You can enter the exchange rate manually.`,
              );
            } else {
              setRateErrorMessage(
                "Unable to fetch the exchange rate. Please enter it manually.",
              );
            }
          }
          setManualInputRequired(true);
        }
      })
      .finally(() => {
        if (!cancelled) setRateLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [currency, financialYearEnd, financialYearStart, yearOfAssessment]);

  // Toggle rate details visibility
  const toggleRateDetails = () => {
    setShowRateDetails(!showRateDetails);
  };

  const sectionCardSx = {
    p: 2,
    border: 1,
    borderColor: "divider",
    borderRadius: 2,
    bgcolor: "background.paper",
  };

  const sectionHeaderSx = {
    display: "flex",
    alignItems: "center",
    gap: 1.25,
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      onExited={onExited}
      maxWidth='sm'
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          p: 2,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <TopicIcon color='primary' sx={{ fontSize: 32 }} />
          <Stack direction='column' spacing={0}>
            <Typography
              color='primary'
              variant='h6'
              sx={{ fontWeight: 600, mb: -0.5 }}
            >
              {isEditMode
                ? "Edit Existing Tax Computation"
                : "Create New Tax Computation"}
            </Typography>
            <Typography
              variant='caption'
              color='text.secondary'
              sx={{ fontWeight: 400 }}
            >
              {isEditMode
                ? "Update the details of the existing tax computation."
                : "Set up a new tax computation for your company."}
            </Typography>
          </Stack>
        </Box>
      </DialogTitle>

      <DialogContent
        sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}
      >
        {/* Basis Period */}
        <Paper
          elevation={0}
          sx={{
            ...sectionCardSx,
            "& .react-datepicker-wrapper, & .react-datepicker__input-container":
              {
                width: "100%",
              },
          }}
        >
          <Box sx={sectionHeaderSx}>
            <EventIcon sx={{ color: "action.active", fontSize: 24 }} />
            <Box>
              <Typography variant='subtitle2' fontWeight={600}>
                Basis Period
              </Typography>
              <Typography variant='caption' color='text.secondary'>
                Set the financial period and related year of assessment.
              </Typography>
            </Box>
          </Box>

          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <DatePicker
                selected={financialYearEnd}
                popperProps={stableDatePickerPopperProps}
                onChange={(date) => {
                  onFormChange("financialYearEnd", date);
                  if (!date) onFormChange("financialYearStart", null);
                }}
                dateFormat='d MMMM yyyy'
                customInput={
                  <TextField
                    fullWidth
                    variant='standard'
                    label='Financial Year End'
                    inputProps={{
                      autoComplete: "off",
                    }}
                  />
                }
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                variant='standard'
                label='Year of Assessment'
                value={yearOfAssessment}
                onChange={(e) => {
                  const value = e.target.value;
                  if (/^\d{0,4}$/.test(value)) {
                    onFormChange("yearOfAssessment", value);
                  }
                }}
                inputProps={{
                  autoComplete: "off",
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  alignItems: { xs: "stretch", sm: "center" },
                  justifyContent: "space-between",
                  gap: 2,
                  p: 1.5,
                  borderRadius: 1.5,
                  bgcolor: "background.paper",
                  border: 1,
                  borderColor: "divider",
                }}
              >
                <Box>
                  <Typography variant='body2' fontWeight={500}>
                    Is this a full 12-month basis period?
                  </Typography>
                  <Typography variant='caption' color='text.secondary'>
                    {isCrossYA
                      ? "Cross-YA requires a basis period longer than 12 months."
                      : "Choose No to enter the financial year start date."}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: { xs: "flex-end", sm: "center" },
                    gap: 0.75,
                    flexShrink: 0,
                  }}
                >
                  <Typography
                    variant='caption'
                    color={
                      resolvedIsFullYear ? "text.secondary" : "primary.main"
                    }
                    fontWeight={resolvedIsFullYear ? 400 : 700}
                  >
                    No
                  </Typography>
                  <Switch
                    checked={resolvedIsFullYear}
                    onChange={(e) =>
                      onFormChange("isFullYear", e.target.checked)
                    }
                    disabled={isCrossYA}
                    inputProps={{
                      "aria-label": "Full 12-month basis period",
                    }}
                  />
                  <Typography
                    variant='caption'
                    color={
                      resolvedIsFullYear ? "primary.main" : "text.secondary"
                    }
                    fontWeight={resolvedIsFullYear ? 700 : 400}
                  >
                    Yes
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>

          <Collapse in={!resolvedIsFullYear} timeout={240} unmountOnExit>
            <Grid container spacing={2} sx={{ mt: 0 }}>
              <Grid item xs={24} sm={12}>
                <DatePicker
                  selected={financialYearStart}
                  disabled={!hasValidFinancialYearEnd}
                  maxDate={financialYearStartMaxDate}
                  popperProps={stableDatePickerPopperProps}
                  onChange={(date) => {
                    if (!hasValidFinancialYearEnd) return;
                    onFormChange("financialYearStart", date);
                  }}
                  autoComplete='off'
                  dateFormat='d MMMM yyyy'
                  customInput={
                    <TextField
                      fullWidth
                      variant='standard'
                      disabled={!hasValidFinancialYearEnd}
                      autoComplete='off'
                      label={
                        isCrossYA
                          ? "Overall Financial Period Start"
                          : "Financial Year Start"
                      }
                      helperText={
                        !hasValidFinancialYearEnd
                          ? "Select the Financial Year End first."
                          : isCrossYA
                            ? "Enter the incorporation date or start of the long financial period."
                            : "Required when the basis period is not a full year."
                      }
                    />
                  }
                />
              </Grid>
            </Grid>
          </Collapse>

          {crossYABasisPeriods && (
            <Box
              sx={{
                mt: 2,
                p: 1.5,
                borderRadius: 1.5,
                bgcolor: "action.hover",
              }}
            >
              <Typography variant='body2' fontWeight={600}>
                Cross-YA basis periods
              </Typography>
              <Typography
                variant='caption'
                color='text.secondary'
                sx={{ display: "block", mb: 1 }}
              >
                {isCustomizedBasisPeriod
                  ? "Using your customized YA split."
                  : "The current YA follows the selected financial year end."}
              </Typography>
              {[crossYABasisPeriods.preceding, crossYABasisPeriods.current].map(
                (period) => (
                  <Box
                    key={period.ya}
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 2,
                      py: 0.25,
                    }}
                  >
                    <Typography variant='body2' fontWeight={500}>
                      YA {period.ya}
                    </Typography>
                    <Typography variant='body2' color='text.secondary'>
                      {formatBasisPeriodDate(period.start)} -{" "}
                      {formatBasisPeriodDate(period.end)}
                    </Typography>
                  </Box>
                ),
              )}

              <Box
                sx={{
                  mt: 1.25,
                  pt: 1,
                  borderTop: 1,
                  borderColor: "divider",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                  }}
                >
                  <Typography variant='caption' color='text.secondary'>
                    Customize basis-period split
                  </Typography>
                  <Switch
                    size='small'
                    checked={Boolean(isCustomizedBasisPeriod)}
                    disabled={!automaticCrossYABasisPeriods}
                    onChange={(event) => {
                      const checked = event.target.checked;
                      onFormChange("isCustomizedBasisPeriod", checked);
                      onFormChange(
                        "basisPeriods",
                        checked
                          ? toBasisPeriodsArray(automaticCrossYABasisPeriods)
                          : [],
                      );
                    }}
                    inputProps={{
                      "aria-label": "Customize Cross-YA basis-period split",
                    }}
                  />
                </Box>

                {isCustomizedBasisPeriod && customizedCrossYABasisPeriods && (
                  <Box sx={{ mt: 0.5 }}>
                    <DatePicker
                      selected={customizedCrossYABasisPeriods.preceding.end}
                      minDate={new Date(financialYearStart)}
                      maxDate={
                        new Date(
                          new Date(financialYearEnd).getFullYear(),
                          new Date(financialYearEnd).getMonth(),
                          new Date(financialYearEnd).getDate() - 1,
                        )
                      }
                      popperProps={stableDatePickerPopperProps}
                      onChange={(date) => {
                        const periods = getCustomizedBasisPeriods(
                          [
                            {
                              yearOfAssessment: Number(yearOfAssessment) - 1,
                              financialYearStart,
                              financialYearEnd: date,
                            },
                            {
                              yearOfAssessment: Number(yearOfAssessment),
                              financialYearStart: date,
                              financialYearEnd,
                            },
                          ],
                          financialYearStart,
                          financialYearEnd,
                          yearOfAssessment,
                        );
                        onFormChange(
                          "basisPeriods",
                          toBasisPeriodsArray(periods),
                        );
                      }}
                      dateFormat='d MMMM yyyy'
                      customInput={
                        <TextField
                          fullWidth
                          size='small'
                          variant='standard'
                          label={`YA ${Number(yearOfAssessment) - 1} End Date`}
                        />
                      }
                    />
                  </Box>
                )}
              </Box>
            </Box>
          )}
        </Paper>

        {/* Type & Category */}
        <Paper elevation={0} sx={sectionCardSx}>
          <Box sx={sectionHeaderSx}>
            <AccountBalanceIcon sx={{ color: "action.active", fontSize: 24 }} />
            <Box>
              <Typography variant='subtitle2' fontWeight={600}>
                Properties
              </Typography>
              <Typography variant='caption' color='text.secondary'>
                Define the filing period structure and computation category.
              </Typography>
            </Box>
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Stack direction='row' alignItems='center' spacing={1}>
                {" "}
                <Typography
                  variant='subtitle2'
                  sx={{ color: "text.secondary" }}
                >
                  Tax Period Type
                </Typography>
                <JustInfo
                  title={
                    <>
                      Single YA Period: The tax computation is calculated based
                      on the full tax period within one YA.
                      <br />
                      Cross-YA Period: The tax computation is apportioned
                      between YAs based on the number of days in each YA.
                    </>
                  }
                />
              </Stack>

              <ToggleButtonGroup
                color='primary'
                exclusive
                fullWidth
                value={taxPeriodType}
                onChange={(e, value) => {
                  if (!value) return;
                  onFormChange("taxPeriodType", value);
                  if (value === "cross_ya") {
                    onFormChange("isFullYear", false);
                  } else {
                    onFormChange("isCustomizedBasisPeriod", false);
                    onFormChange("basisPeriods", []);
                  }
                }}
                sx={{ mt: 1 }}
              >
                <ToggleButton value='single_ya' sx={{ textTransform: "none" }}>
                  Single YA Period
                </ToggleButton>
                <ToggleButton value='cross_ya' sx={{ textTransform: "none" }}>
                  Cross-YA Period
                </ToggleButton>
              </ToggleButtonGroup>
              {crossYAPeriodError && (
                <Typography
                  variant='caption'
                  color='error'
                  sx={{ display: "block", mt: 1 }}
                >
                  {crossYAPeriodError}
                </Typography>
              )}
            </Grid>

            <Grid item xs={12}>
              <Stack direction='row' alignItems='center' spacing={1}>
                <Typography
                  variant='subtitle2'
                  sx={{ color: "text.secondary" }}
                >
                  Tax Computation Category
                </Typography>
                <JustInfo
                  title={
                    isEditMode ? (
                      <>
                        Tax computation category cannot be changed in edit mode.
                        If you need to modify this, please create a new tax
                        computation with the desired category.
                      </>
                    ) : (
                      <>
                        Trading: For companies primarily engaged in trading or
                        business activities.
                        <br />
                        Investment Holding: For companies primarily engaged in
                        investment holding activities, such as managing
                        investments, assets, or securities.
                      </>
                    )
                  }
                />
              </Stack>
              {isEditMode ? (
                <Box
                  sx={{
                    mt: 1,
                    display: "flex",
                    alignItems: "center",
                    p: 1.5,
                    borderRadius: 1.5,
                    border: 1,
                    borderColor: "divider",
                    bgcolor: "background.paper",
                    justifyContent: "space-between",
                  }}
                >
                  <Typography variant='body2' fontWeight={600}>
                    Your selected type is
                  </Typography>

                  <Chip
                    label={
                      taxComputationType === "common"
                        ? "Trading"
                        : "Investment Holding"
                    }
                    sx={{
                      borderRadius: 1,
                      fontWeight: 700,
                      textTransform: "capitalize",
                      color: "primary.main",
                      height: 32,
                      "& .MuiChip-label": {
                        px: 1.5,
                      },
                    }}
                  />
                </Box>
              ) : (
                <ToggleButtonGroup
                  color='primary'
                  exclusive
                  fullWidth
                  value={taxComputationType}
                  onChange={(e, value) => {
                    if (value) onFormChange("taxComputationType", value);
                  }}
                  sx={{ mt: 1 }}
                  disabled={isEditMode}
                >
                  <ToggleButton value='common' sx={{ textTransform: "none" }}>
                    Trading
                  </ToggleButton>
                  <ToggleButton
                    value='investment'
                    sx={{ textTransform: "none" }}
                  >
                    Investment Holding
                  </ToggleButton>
                </ToggleButtonGroup>
              )}
            </Grid>
          </Grid>
        </Paper>

        {/* Currency */}
        <Paper elevation={0} sx={sectionCardSx}>
          <Box sx={sectionHeaderSx}>
            <AttachMoneyIcon sx={{ color: "action.active", fontSize: 24 }} />
            <Box>
              <Typography variant='subtitle2' fontWeight={600}>
                Currency
              </Typography>
              <Typography variant='caption' color='text.secondary'>
                Select the reporting currency and exchange rate to SGD.
              </Typography>
            </Box>
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Autocomplete
                options={currencyList}
                getOptionLabel={(option) =>
                  option ? `${option.code} - ${option.currency}` : ""
                }
                value={currency}
                onChange={(event, newValue) => {
                  onFormChange("currency", newValue);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant='standard'
                    label='Currency'
                    placeholder='Select Currency'
                    fullWidth
                  />
                )}
                isOptionEqualToValue={(option, value) => {
                  return option?.code === value?.code;
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ width: "100%" }}>
                {/* Currency Rate Input */}
                <TextField
                  fullWidth
                  variant='standard'
                  label='Currency Rate (to SGD)'
                  type='number'
                  value={currencyRate || ""}
                  onChange={(e) => onFormChange("currencyRate", e.target.value)}
                  disabled={currency?.code === "SGD"}
                  inputProps={{
                    autoComplete: "off",
                    step: "0.0001",
                    min: "0",
                  }}
                  helperText={
                    currency?.code === "SGD"
                      ? "SGD does not require exchange rate"
                      : rateErrorMessage
                        ? rateErrorMessage
                        : manualInputRequired
                          ? "API only provides previous year's rates. Please enter manually."
                          : masCurrencyRate
                            ? "Rate fetched from MAS"
                            : ""
                  }
                />

                {/* Loading Indicator */}
                {rateLoading && (
                  <Box
                    sx={{
                      mt: 1,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <CircularProgress size={16} />
                    <Typography variant='caption' color='text.secondary'>
                      Fetching exchange rate...
                    </Typography>
                  </Box>
                )}

                {/* Rate Details Toggle Button */}
                {masCurrencyRate &&
                  masCurrencyRate.details &&
                  !manualInputRequired && (
                    <Button
                      onClick={toggleRateDetails}
                      size='small'
                      sx={{ mt: 1, textTransform: "none", minWidth: "auto" }}
                      endIcon={
                        showRateDetails ? <ExpandLess /> : <ExpandMore />
                      }
                    >
                      {showRateDetails ? "Hide" : "View"} Monthly Rate Details (
                      {masCurrencyRate.details.length} months)
                    </Button>
                  )}

                {/* Rate Details Table */}
                {showRateDetails && masCurrencyRate?.details && (
                  <Paper
                    sx={{
                      mt: 2,
                      p: 2,
                      bgcolor: "background.default",
                      borderRadius: 2,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 1,
                      }}
                    >
                      <Typography variant='subtitle2' fontWeight={600}>
                        Exchange Rate Details
                      </Typography>
                      <Typography variant='caption' color='text.secondary'>
                        {masCurrencyRate.currency} → SGD
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mb: 2,
                      }}
                    >
                      <Typography variant='caption' color='text.secondary'>
                        <strong>Average Rate:</strong> {masCurrencyRate.rate}
                      </Typography>
                      <Typography variant='caption' color='text.secondary'>
                        <strong>Period:</strong>{" "}
                        {masCurrencyRate.monthsIncluded}/
                        {masCurrencyRate.totalMonths} months
                      </Typography>
                    </Box>

                    <TableContainer sx={{ maxHeight: 200 }}>
                      <Table size='small' stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ py: 1, px: 1 }}>Month</TableCell>
                            <TableCell sx={{ py: 1, px: 1 }}>Type</TableCell>
                            <TableCell sx={{ py: 1, px: 1 }} align='right'>
                              Rate
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {masCurrencyRate.details.map((detail, index) => (
                            <TableRow
                              key={index}
                              sx={{
                                "&:last-child td, &:last-child th": {
                                  border: 0,
                                },
                                bgcolor:
                                  index % 2 === 0
                                    ? "transparent"
                                    : "action.hover",
                              }}
                            >
                              <TableCell sx={{ py: 0.5, px: 1 }}>
                                {detail.month}
                              </TableCell>
                              <TableCell sx={{ py: 0.5, px: 1 }}>
                                <Chip
                                  label={
                                    detail.type === "full" ? "Monthly" : "Daily"
                                  }
                                  size='small'
                                  variant='outlined'
                                  sx={{ height: 20, fontSize: "0.7rem" }}
                                />
                              </TableCell>
                              <TableCell sx={{ py: 0.5, px: 1 }} align='right'>
                                {detail.rate}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>

                    {masCurrencyRate.basisPeriod && (
                      <Box
                        sx={{
                          mt: 1,
                          pt: 1,
                          borderTop: 1,
                          borderColor: "divider",
                        }}
                      >
                        <Typography variant='caption' color='text.secondary'>
                          <strong>Basis Period:</strong>{" "}
                          {new Date(
                            masCurrencyRate.basisPeriod.start,
                          ).toLocaleDateString()}{" "}
                          -{" "}
                          {new Date(
                            masCurrencyRate.basisPeriod.end,
                          ).toLocaleDateString()}
                        </Typography>
                      </Box>
                    )}
                  </Paper>
                )}
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </DialogContent>

      <DialogActions sx={{ mt: 2 }}>
        <Button
          onClick={onClose}
          color='secondary'
          disabled={submitLoading}
          sx={{
            textTransform: "none",
            fontWeight: 500,
            borderRadius: 2,
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={onSubmit}
          variant='contained'
          color='primary'
          disabled={
            submitLoading || !hasValidFinancialPeriod || !hasValidCrossYAPeriod
          }
          startIcon={
            submitLoading ? (
              <CircularProgress size={18} color='inherit' sx={{ mr: 0.5 }} />
            ) : null
          }
          sx={{
            textTransform: "none",
            fontWeight: 500,
            borderRadius: 2,
            boxShadow: "0 2px 6px rgba(0, 0, 0, 0.15)",
            "&:hover": {
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.2)",
            },
          }}
        >
          {submitLoading
            ? isEditMode
              ? "Updating..."
              : "Adding..."
            : isEditMode
              ? "Update"
              : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TaxComputationDialog;
